import Papa from "papaparse";

/**
 * Pure, DOM-free tabular parsing/formatting helpers.
 *
 * This module must import NOTHING besides `papaparse`. It is loaded both by
 * the main thread (via re-exports from `helpers.ts`) and by the report-table
 * Web Worker (`workers/report-table.worker.ts`), which has no `window` — only
 * `self`. `helpers.ts` imports `Config` from `@via/utils/config.tsx`, which
 * reads `window.VIA_RUNTIME_CONFIG` at module scope, so importing `helpers.ts`
 * from a worker throws `ReferenceError: window is not defined` at boot.
 *
 * The "tab-delimited files are not number-formatted" rule and the header-
 * normalisation rule each live here and only here, so the main-thread path
 * and the worker path cannot drift apart.
 */

/**
 * PERF: only the first line determines the delimiter, so this must not
 * `split("\n")` over the whole file — on a 43MB report that allocates an
 * array holding every line just to look at the first one. This function is
 * called multiple times per parse (inside `getColumnsTabularData`, directly
 * by the report-table worker, and again inside `cleanTabularDataBeforeRender`),
 * so keeping it an O(first line) scan via `indexOf` rather than an O(file)
 * split matters for every one of those call sites, not just one.
 */
export const getDelimiterTabularData = (text: string) => {
  const firstNewline = text.indexOf("\n");
  const firstLine = firstNewline === -1 ? text : text.slice(0, firstNewline);
  let delimiter = ",";
  if (firstLine.indexOf("\t") !== -1) {
    delimiter = "\t";
  }
  return delimiter;
};

/**
 * Normalises a header row: papaparse-safe column names.
 * Extracted so the streaming worker applies exactly the same rules as the
 * whole-text path.
 */
export const normalizeHeaderCells = (headers: string[]): string[] => {
  const normalized = headers.map(header => String(header).replace(/\./g, "_"));
  // An empty first column breaks client-side rendering; give it a space.
  if (normalized[0] === "") {
    normalized[0] = " ";
  }
  return normalized;
};

export const getColumnsTabularData = (text: string) => {
  const delimiter = getDelimiterTabularData(text);
  const headerText = text.split("\n")[0];
  let headersArray = [] as Array<string>;
  if (delimiter == "\t") {
    headersArray = headerText.split(delimiter);
  } else {
    try {
      const parsed = Papa.parse(headerText, {
        delimiter: delimiter,
        header: false,
        skipEmptyLines: true,
      });
      if (parsed.data && parsed.data[0]) {
        headersArray = (parsed.data[0] as Array<string>).map(item =>
          String(item)
        );
      }
    } catch (err) {
      console.log("Error parsing line:", headerText, err);
      headersArray = headerText.split(delimiter);
    }
  }

  return normalizeHeaderCells(headersArray);
};

/**
 * Shared number formatter.
 *
 * PERF: `value.toLocaleString("en-US", { ...options })` constructs a fresh
 * Intl.NumberFormat on every call. On run 8942's merged_table.csv (688,712
 * cells) that cost 12,645 ms of a 13,160 ms render. Hoisting one formatter and
 * calling .format() brings the same work to 382 ms — a 33x reduction with
 * byte-identical output (verified: 0 of 14,972 rows differ).
 */
const NUMBER_FORMATTER = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/**
 * Formats a numeric string with thousands separators and at most 2 decimals.
 * Non-numeric input and scientific notation are returned unchanged.
 *
 * NOTE: formatNumber("") returns "NaN" (Number("") is 0, but parseFloat("") is
 * NaN). This is long-standing behaviour frozen by the parity test suite (see
 * PARITY_CASES in frontend/src/__tests__/unit/reports/report-helpers.test.ts);
 * changing it is out of scope here.
 */
export const formatNumber = (value: string) => {
  if (!isNaN(Number(value)) && typeof value === "string") {
    const numberValue = parseFloat(value);
    if (numberValue.toString().includes("e")) {
      return numberValue.toString();
    }
    return NUMBER_FORMATTER.format(numberValue);
  }
  return value;
};

/**
 * Applies the per-cell formatting rule for one parsed row.
 *
 * Tab-delimited reports are intentionally left unformatted — long-standing
 * behaviour. This function is the ONLY place that rule lives; the streaming
 * worker (report-table.worker.ts) imports it so the two parse paths cannot
 * drift apart.
 */
export const formatRowCells = (
  row: string[],
  isTabDelimited: boolean
): string[] => (isTabDelimited ? row : row.map(formatNumber));

/**
 * Parses data rows that do NOT include a header line.
 *
 * Extracted from `cleanTabularDataBeforeRender` so the streaming worker
 * (`report-table.worker.ts`) can parse the response body segment-by-segment
 * as it arrives, instead of requiring the whole file in memory before a
 * single row can be parsed: it slices the incoming text at safe newline
 * boundaries and calls this once per segment. `columns` and `delimiter` are
 * supplied by the caller — derived once, from the first line, via
 * `getColumnsTabularData` / `getDelimiterTabularData` — rather than
 * re-derived here, because a data-only segment has no header row to derive
 * them from.
 *
 * `cleanTabularDataBeforeRender` delegates to this function (after slicing
 * off its own header line) so the tab-quote-naive rule and the number
 * formatting rule each keep exactly ONE home, used by both the whole-file
 * and the streaming paths.
 */
export const parseDataRows = (
  text: string,
  columns: string[],
  delimiter: string
): Record<string, unknown>[] => {
  const isTab = delimiter === "\t";
  const data: Record<string, unknown>[] = [];

  if (text.length === 0) {
    return data;
  }

  if (isTab) {
    // Tab-delimited files are parsed by hand, one line at a time, and are
    // completely quote-naive on purpose. Papaparse treats `"` as a quote
    // character regardless of the configured delimiter, so a TSV field that
    // starts with a literal `"` (e.g. a genomics annotation like `"5" arm`)
    // would be swallowed up to the next unmatched quote, merging subsequent
    // rows/lines into a single garbage cell if run through Papa.parse. Do
    // not route this branch through Papa.parse.
    const lines = text.split("\n");
    for (let i = 0; i < lines.length; i++) {
      const cleanedContent = lines[i].replace(/\r/g, "");
      const cells = formatRowCells(cleanedContent.split(delimiter), true);
      const formatedData: Record<string, unknown> = {};
      for (let j = 0; j < cells.length; j++) {
        formatedData[columns[j]] = cells[j];
      }
      if (Object.keys(formatedData).length > 0) {
        data.push(formatedData);
      }
    }
    return data;
  }

  // PERF: one parse per segment rather than one parse per row. Papaparse
  // handles CRLF and quoted fields itself, so no manual \r strip is needed
  // here for comma-delimited text.
  //
  // ACCEPTED DIVERGENCES from the old per-line implementation (comma-
  // delimited files only) — intentional consequences of parsing more than
  // one line per pass, do not "fix" these:
  //  - A quoted field containing an embedded newline stays one field on one
  //    row, instead of being split across two rows by a naive per-line
  //    split. Row counts change only for such files.
  //  - A bare \r inside a quoted field is preserved rather than stripped.
  const parsed = Papa.parse<string[]>(text, {
    delimiter,
    header: false,
    skipEmptyLines: true,
  });

  const rows = parsed.data ?? [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!Array.isArray(row) || row.length === 0) {
      continue;
    }
    const cells = formatRowCells(row, false);
    const formatedData: Record<string, unknown> = {};
    for (let j = 0; j < cells.length; j++) {
      formatedData[columns[j]] = cells[j];
    }
    if (Object.keys(formatedData).length > 0) {
      data.push(formatedData);
    }
  }

  return data;
};

export const cleanTabularDataBeforeRender = (
  text: string,
  columns: Array<string>
) => {
  // PERF: one parse for the whole file rather than one parse per row.
  // 15k parser allocations removed; peak heap 165MB -> 95MB on run 8942.
  const delimiter = getDelimiterTabularData(text);
  const trimmed = text.trim();
  const firstNewline = trimmed.indexOf("\n");
  // No newline at all means the whole (trimmed) text is just the header
  // line, with no data rows below it.
  const dataText = firstNewline === -1 ? "" : trimmed.slice(firstNewline + 1);
  return parseDataRows(dataText, columns, delimiter);
};

/**
 * Projects column-keyed records (as returned by `cleanTabularDataBeforeRender`)
 * into arrays-of-arrays, one array per record, cell order matching `columns`.
 *
 * Extracted as a pure function so the row-shaping the report-table Web Worker
 * performs — converting `cleanTabularDataBeforeRender`'s record shape into the
 * structured-clone-cheap array shape the worker posts to the main thread (see
 * `workers/report-table-protocol.ts`) — can be unit-tested without a real
 * `Worker` transport (jsdom provides none).
 *
 * A record missing a key entirely (e.g. a malformed short row with fewer
 * cells than headers) yields "" for that cell rather than the string
 * "undefined". This does not affect the documented empty-cell -> "NaN" quirk:
 * that string is already baked into the record's value by
 * `cleanTabularDataBeforeRender` (via `formatNumber("")`) whenever the row
 * DID have an empty field for that column; it is preserved here unchanged
 * because this function never re-derives cell values, only reorders them.
 */
export const recordsToRowArrays = (
  records: Array<Record<string, unknown>>,
  columns: string[]
): string[][] =>
  records.map(record =>
    columns.map(column => {
      const value = record[column];
      return value === undefined ? "" : String(value);
    })
  );

/**
 * A byte-Range request cuts the file mid-line. Drop everything after the last
 * newline so the caller only ever parses complete records.
 *
 * NOTE: also trims a trailing \r before that newline, so a CRLF-terminated
 * file doesn't leave a stray \r dangling on the new last line.
 */
export const dropTrailingPartialLine = (text: string): string => {
  const lastNewline = text.lastIndexOf("\n");
  if (lastNewline === -1) {
    return "";
  }
  const end = text[lastNewline - 1] === "\r" ? lastNewline - 1 : lastNewline;
  return text.slice(0, end);
};
